import {
  ORDERS_TO_APPROVE_FORM_NAME,
  ORDERS_TO_APPROVE_PAGE_PATH,
  STATUS_FAIL,
  SYSTEM_ERROR
} from '../../components/common/analytics/analyticsConstants';
import {
  getObjectAsQueryParams,
  replaceTokensInString,
  replaceTokensInUrl
} from '../../utils';
import { ACTION_TYPE, TIMEOUT_EXTENDED } from '../../constants/commonConstants';
import endpoints from '../../constants/endpoints';
import { getHttpInstance } from '../../services/defaultHttpService';
import * as types from './constants';
import { setError, clearError } from '../exception/actions';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  ERROR_PLACE_ORDER_FAILURE
} from '@app/constants/errorConstants';

export const getOrdersToApprove =
  ({
    props,
    setError,
    domain,
    path,
    clearError,
    fireFormSubmittedEvent,
    t,
    onSuccessGAEvent,
    onFailGAEvent
  }) =>
  async (dispatch, getState) => {
    const { storeId, userId: approverId, langId } = getState().common;
    const config = {
      headers: { 'Cache-Control': 'no-cache' }
    };

    const {
      entityId,
      priceRangeMax,
      priceRangeMin,
      displayStartDate,
      displayEndDate,
      statusDisplayName = 'Pending'
    } = props;
    const analyticData = `Ref: ${entityId} | Date: ${displayStartDate} ${displayEndDate} | Sub: ${priceRangeMin} ${priceRangeMax} | Status: ${statusDisplayName}`;
    const eventOptions = {
      pagePath: ORDERS_TO_APPROVE_PAGE_PATH,
      formName: ORDERS_TO_APPROVE_FORM_NAME,
      formContent: analyticData,
      formLocation: ''
    };

    const queryParams = getObjectAsQueryParams({
      storeId,
      approverId,
      responseFormat: 'json',
      q: 'orderApprovals',
      langId,
      ...props
    });

    const url = replaceTokensInUrl(
      `${endpoints.ORDERS_TO_APPROVE}?${queryParams}`,
      storeId
    );

    dispatch({ type: types.ORDERS_TO_APPROVE_GET_BEGIN });

    const http = getHttpInstance(TIMEOUT_EXTENDED);
    return http
      .get(url, config)
      .then(response => {
        const {
          resultList = [],
          recordSetCount,
          pendingOrderCount,
          expiringSoonOrderCount,
          ordersToApproveDcn
        } = response.data;

        const orders = resultList.reduce((prev, current) => {
          if (!current) {
            return prev;
          }

          const {
            order,
            status: { statusName, id: statusId },
            submitter,
            accountNumber,
            ...rest
          } = current;

          const { firstName, lastName } = submitter;
          const submitterDisplayName = `${firstName ?? ''} ${lastName ?? ''}`
            .trim()
            .slice(0, 40);

          return {
            ...prev,
            [current.orderId]: {
              ...order,
              ...submitter,
              ...rest,
              statusName,
              statusId,
              submitterDisplayName,
              account: accountNumber
            }
          };
        }, {});

        dispatch({
          type: types.ORDERS_TO_APPROVE_GET_SUCCESS,

          payload: {
            orders,
            recordSetCount,
            pendingOrderCount,
            expiringSoonOrderCount,
            ordersToApproveDcn
          }
        });
        clearError && clearError(domain, path);
        fireFormSubmittedEvent &&
          fireFormSubmittedEvent({
            ...eventOptions,
            formContent2: `Results:${recordSetCount}`,
            formStatus: recordSetCount === 0 ? 'fail' : 'success',
            formFieldCausingError: recordSetCount === 0 ? 'no results' : ''
          });
        if (!resultList.length && recordSetCount === 0 && setError) {
          setError(domain, ERROR_PATH.ORDERS_TO_APPROVE_SEARCH_TABLE, {
            message: t('CAL_Error_2')
          });
        }
        if (onSuccessGAEvent) {
          onSuccessGAEvent(props, recordSetCount);
        }
        return true;
      })
      .catch(err => {
        dispatch({
          type: types.ORDERS_TO_APPROVE_GET_FAIL,
          payload: err.message
        });
        setError &&
          setError(domain, path, { message: t('UNKNOWN_ERROR_MESSAGE') });
        fireFormSubmittedEvent &&
          fireFormSubmittedEvent({
            ...eventOptions,
            formStatus: STATUS_FAIL,
            formFieldCausingError: SYSTEM_ERROR
          });
        if (onFailGAEvent) {
          onFailGAEvent(props);
        }
        return false;
      });
  };

export const setOrdersToApproveFormValues = props => ({
  type: types.ORDERS_TO_APPROVE_SEARCH_FORM_SET,
  payload: props
});

export const getPendingOrdersToApproveNumber =
  () => async (dispatch, getState) => {
    const { storeId } = getState().common;
    const config = {
      headers: { 'Cache-Control': 'no-cache' }
    };
    const queryParams = getObjectAsQueryParams({
      responseFormat: 'json'
    });

    const url = replaceTokensInUrl(
      `${endpoints.ORDERS_TO_APPROVE_PENDING_COUNT}?${queryParams}`,
      storeId
    );

    dispatch({ type: types.ORDERS_PENDING_TO_APPROVE_NUMBER_BEGIN });

    const http = getHttpInstance();
    return http
      .get(url, config)
      .then(response =>
        dispatch({
          type: types.ORDERS_PENDING_TO_APPROVE_NUMBER_SUCCESS,
          payload: response?.data
        })
      )
      .catch(err =>
        dispatch({
          type: types.ORDERS_PENDING_TO_APPROVE_NUMBER_FAIL
        })
      );
  };

const catchHandler = (err, dispatch) => {
  const error = err?.response?.data?.errors && err?.response?.data?.errors[0];
  const errorMessage = error?.errorMessage;
  const errorKey = error?.errorKey;

  dispatch({
    type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_FAIL,
    payload: errorMessage ?? errorKey
  });
};

export const thenHandler = (
  params,
  dispatch,
  ToastCtx,
  toastMessage,
  onSuccess
) => {
  const orderId = params.orderId;
  dispatch({
    type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_SUCCESS,
    payload: {
      orderId,
      statusName: params.statusName,
      statusId: params.statusId
    }
  });
  dispatch(getPendingOrdersToApproveNumber());
  !!toastMessage && ToastCtx.createSuccessToast(toastMessage);
  if (onSuccess) {
    onSuccess();
  }
};

const executePlacePendingOrder = ({
  additionalPaymentMethod,
  approvalStatusId,
  billingAddressId,
  catalogId,
  langId,
  orderId,
  paymentGateway,
  paymentMethod,
  poNumber,
  storeId
}) => {
  const endpointUrl = replaceTokensInUrl(
    endpoints.PLACE_PENDING_ORDER,
    storeId,
    langId,
    approvalStatusId
  );
  return getHttpInstance(TIMEOUT_EXTENDED).post(endpointUrl, {
    billingAddressId,
    catalogId,
    orderId,
    paymentMethod,
    additionalPaymentMethod,
    paymentGateway,
    poNumber,
    quoteOrder: 'false'
  });
};

export const placePendingOrder = (
  {
    approvalStatusId = null,
    billingAddressId,
    error = ERROR_PLACE_ORDER_FAILURE,
    orderId,
    paymentGateway,
    paymentMethod,
    poNumber = ''
  },
  failCallback
) => {
  return async (dispatch, getState) => {
    dispatch(clearError(ERROR_DOMAIN.PENDING_ORDER_DETAILS));
    dispatch({ type: types.PLACE_PENDING_ORDER_BEGIN });
    const {
      common: { catalogId, langId, storeId }
    } = getState();

    try {
      const placeOrderResult = await executePlacePendingOrder({
        approvalStatusId,
        billingAddressId,
        catalogId,
        langId,
        orderId,
        paymentGateway,
        paymentMethod,
        poNumber,
        storeId
      });
      const { data: payload } = placeOrderResult;
      dispatch({
        type: types.PLACE_PENDING_ORDER_SUCCESS,
        payload
      });
      return payload;
    } catch (e) {
      dispatch({
        type: types.PLACE_PENDING_ORDER_FAIL
      });
      if (failCallback) {
        failCallback();
      }
      dispatch(
        setError(ERROR_DOMAIN.PENDING_ORDER_DETAILS, ERROR_PATH.MAIN, error)
      );
    }
  };
};

/**
 * @param {string} orderId
 * @param {string} approvalStatusId
 * @param {string} type - it will be approve or reject
 * @param {string} comments
 * **/
export const approveRejectOrder =
  ({ orderId, approvalStatusId, type = ACTION_TYPE.approve, comments }) =>
  async (dispatch, getState) => {
    const { storeId, langId, catalogId } = getState().common;

    const http = getHttpInstance(TIMEOUT_EXTENDED);

    const url = replaceTokensInUrl(
      endpoints.APPROVE_REJECT_ORDER,
      storeId,
      catalogId,
      langId,
      orderId,
      approvalStatusId
    );

    const param = {
      action: type,
      comments: comments ?? ''
    };

    const config = {
      headers: { 'Cache-Control': 'no-cache' }
    };
    dispatch({
      type: types.APPROVE_REJECT_ORDER_TO_APPROVE_BEGIN
    });

    try {
      const { data = {} } = await http.post(url, param, config);
      dispatch({
        type: types.APPROVE_REJECT_ORDER_TO_APPROVE_SUCCESS
      });
      return data;
    } catch (error) {
      dispatch({
        type: types.APPROVE_REJECT_ORDER_TO_APPROVE_FAIL
      });
      throw error;
    }
  };

/**
 * @param {string} orderId
 * @param {string} approvalStatusId
 * @param {string} comments
 * **/
export const deletePendingOrder =
  ({ orderId, approvalStatusId, comments }) =>
  async (dispatch, getState) => {
    const { storeId, langId, catalogId } = getState().common;

    const http = getHttpInstance();

    const url = replaceTokensInUrl(
      endpoints.DELETE_PENDING_ORDER,
      storeId,
      catalogId,
      langId,
      orderId,
      approvalStatusId
    );

    const config = {
      headers: { 'Cache-Control': 'no-cache' },
      data: {
        comments
      }
    };

    dispatch({
      type: types.DELETE_PENDING_ORDER_BEGIN
    });

    try {
      const { data = {} } = await http.delete(url, config);
      dispatch({
        type: types.DELETE_PENDING_ORDER_SUCCESS
      });
      return data;
    } catch (error) {
      dispatch({
        type: types.DELETE_PENDING_ORDER_FAIL
      });
      throw error;
    }
  };

export const setChangeOrderStatus =
  (
    params,
    ToastCtx,
    toastMessage,
    setError,
    clearError,
    t,
    onSuccess,
    isCCOrdersToApprove,
    onFail
  ) =>
  async (dispatch, getState) => {
    const { storeId, langId, catalogId } = getState().common;

    dispatch({
      type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_BEGIN,
      payload: { changeOrderActionId: params.statusId }
    });

    const approveRejectParams = {
      orderId: params.orderId,
      approvalStatusId: params.approvalStatusId,
      type: params?.type
    };

    const onSuccessParams = {
      storeId,
      catalogId,
      langId,
      orderId: params.orderId
    };

    return dispatch(approveRejectOrder(approveRejectParams))
      .then(_ => {
        thenHandler(params, dispatch, ToastCtx, toastMessage, () =>
          onSuccess(onSuccessParams)
        );
      })
      .catch(err => {
        catchHandler(err, dispatch);
        onFail(
          dispatch,
          setError,
          clearError,
          isCCOrdersToApprove,
          t,
          params.orderId
        );
      });
  };

export const setDeleteOrderStatus =
  (
    params,
    ToastCtx,
    toastMessage,
    setError,
    clearError,
    t,
    onSuccess,
    isCCOrdersToApprove,
    onFail
  ) =>
  async (dispatch, getState) => {
    dispatch({
      type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_BEGIN,
      payload: { changeOrderActionId: params.statusId }
    });

    const deletePendingParams = {
      orderId: params.orderId,
      approvalStatusId: params.approvalStatusId,
      comments: params.comments
    };

    return dispatch(deletePendingOrder(deletePendingParams))
      .then(res => {
        thenHandler(params, dispatch, ToastCtx, toastMessage, onSuccess);
        return res;
      })
      .catch(error => {
        catchHandler(error, dispatch);
        onFail(
          dispatch,
          setError,
          clearError,
          isCCOrdersToApprove,
          t,
          params.orderId
        );
      });
  };
export const setCloseOrderSystemFeedback = ordersToApproveErrorDismiss => {
  return {
    type: types.AC_ORDERS_TO_APPROVE_SYSTEMFEEDBACK,
    payload: ordersToApproveErrorDismiss
  };
};

export const editOrder =
  ({ orderId }) =>
  async (dispatch, getState) => {
    const { storeId, langId, catalogId } = getState().common;

    const http = getHttpInstance();

    const editUrl = replaceTokensInUrl(
      endpoints.XORDER_EDIT_ORDER,
      storeId,
      catalogId,
      langId,
      orderId
    );

    // In order to update cart from pending order we need to set the dealer organization session #387278
    const setDealerOrganizationUrl = replaceTokensInUrl(
      endpoints.DEALER_ORGANIZATION_SET_IN_SESSION,
      storeId
    );
    dispatch({
      type: types.ABLE_TO_EDIT_ORDER_BEGIN
    });

    try {
      const editResponse = await http.put(editUrl);
      const dealerOrganizationData = await http.get(setDealerOrganizationUrl);

      if (
        editResponse.status !== 204 &&
        dealerOrganizationData.success !== true
      ) {
        throw new Error();
      }
      dispatch({
        type: types.ABLE_TO_EDIT_ORDER_SUCCESS
      });
    } catch (error) {
      dispatch({
        type: types.ABLE_TO_EDIT_ORDER_FAILURE
      });
    }
  };

export const setEditStatusToIDLE = () => {
  return {
    type: types.SET_EDIT_STATUS_TO_IDLE
  };
};

// saves requestByDate, Equipment and poNumber
export const savePONumberEquipmentAndRBD =
  ({ equipmentInfo, orderId, requestByDate, serialNumber, poNumber }) =>
  async (dispatch, getState) => {
    const { storeId, langId, locale } = getState().common;

    const http = getHttpInstance();

    const xOrderAttributesUrl = replaceTokensInUrl(
      endpoints.XORDER_ATTRIBUTES,
      storeId,
      langId,
      orderId
    );

    const queryParams = getObjectAsQueryParams({
      orderId,
      locale,
      storeId,
      poNumber
    });
    const xOrderPoAttachment = replaceTokensInString(
      `${endpoints.XORDER_PO_ATTACHMENT}?${queryParams}`
    );

    const data = {
      requestByDate,
      equipmentInfo
    };

    dispatch({
      type: types.SAVE_PONUMBER_SERIAL_AND_RBD_BEGIN
    });
    try {
      const { status: xorderPoAttachmentStatus } = await http.put(
        xOrderPoAttachment
      );
      const { status: xorderAttributeStatus } = await http.patch(
        xOrderAttributesUrl,
        data
      );
      if (xorderAttributeStatus === 204 && xorderPoAttachmentStatus === 204) {
        dispatch({
          type: types.SAVE_PONUMBER_SERIAL_AND_RBD_SUCCESS
        });
      }
    } catch (error) {
      dispatch({
        type: types.SAVE_PONUMBER_SERIAL_AND_RBD_FAIL
      });
      throw error;
    }
  };

export const deleteOrderStatus =
  (
    { orderId, approvalStatusId, comments, statusName, statusId },
    onSuccess,
    onError
  ) =>
  async dispatch => {
    dispatch({
      type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_BEGIN,
      payload: { orderId }
    });
    const deletePendingParams = {
      orderId,
      approvalStatusId,
      comments
    };
    return dispatch(deletePendingOrder(deletePendingParams))
      .then(() => {
        dispatch({
          type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_SUCCESS,
          payload: {
            orderId,
            statusName,
            statusId
          }
        });
        dispatch(getPendingOrdersToApproveNumber());
        if (onSuccess) {
          onSuccess();
        }
        return true;
      })
      .catch(err => {
        onError && onError();
        dispatch({
          type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_FAIL,
          payload: { orderId }
        });
        return false;
      });
  };

export const handleOrderStatusChange =
  (
    { orderId, approvalStatusId, type, statusId, statusName },
    onSuccess,
    onError
  ) =>
  async dispatch => {
    dispatch({
      type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_BEGIN,
      payload: { changeOrderActionId: statusId, orderId }
    });
    const approveRejectParams = {
      orderId: orderId,
      approvalStatusId: approvalStatusId,
      type: type
    };

    return dispatch(approveRejectOrder(approveRejectParams))
      .then(() => {
        dispatch({
          type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_SUCCESS,
          payload: {
            orderId,
            statusName: statusName,
            statusId: statusId
          }
        });
        if (onSuccess) {
          onSuccess();
        }
        dispatch(getPendingOrdersToApproveNumber());
        return true;
      })
      .catch(err => {
        dispatch({
          type: types.ORDERS_TO_APPROVE_STATUS_CHANGE_FAIL,
          payload: { orderId }
        });
        onError && onError();
        return false;
      });
  };
